/**
 * API endpoint paths, relative to the base URL (env.VITE_API_URL === '/api/v1').
 * The RTK Query baseQuery prepends the base — never inline an endpoint string in
 * an API slice. Trailing slashes mirror tap-api routing exactly to avoid 307s.
 *
 * Functions build paths that contain a path parameter.
 */
export const ENDPOINTS = {
  AUTH: {
    LOGIN: '/auth/login',
    REFRESH: '/auth/refresh',
    LOGOUT: '/auth/logout',
    FORGOT_PASSWORD: '/auth/forgot-password',
    RESET_PASSWORD: '/auth/reset-password',
  },
  USERS: {
    ROOT: '/users/',
    BY_ID: (id: string) => `/users/${id}`,
  },
  ROLES: {
    ROOT: '/roles/',
    BY_ID: (id: string) => `/roles/${id}`,
  },
  PERMISSIONS: {
    ROOT: '/permissions/',
    /** The caller's own effective permissions — readable by any authenticated user. */
    MINE: '/permissions/me',
    FOR_ROLE: (roleId: string) => `/permissions/roles/${roleId}`,
  },
  TENANTS: {
    ROOT: '/tenants/',
    CURRENT: '/tenants/current',
    BY_ID: (id: string) => `/tenants/${id}`,
  },
  PARTNERS: {
    ROOT: '/partners/',
    BY_ID: (id: string) => `/partners/${id}`,
    CUSTOMERS: (id: string) => `/partners/${id}/customers`,
  },
  ASSETS: {
    ROOT: '/assets/',
    TREE: '/assets/tree',
    BY_ID: (id: string) => `/assets/${id}`,
  },
  GATEWAYS: {
    ROOT: '/gateways/',
    BY_ID: (id: string) => `/gateways/${id}`,
    STATUS: (id: string) => `/gateways/${id}/status`,
  },
  TAGS: {
    ROOT: '/tags/',
    BY_ID: (id: string) => `/tags/${id}`,
    VALUE: (id: string) => `/tags/${id}/value`,
    HISTORY: (id: string) => `/tags/${id}/history`,
  },
  ALARMS: {
    RULES: '/alarms/rules',
    RULE_BY_ID: (id: string) => `/alarms/rules/${id}`,
    EVENTS: '/alarms/events',
    ACKNOWLEDGE: (id: string) => `/alarms/events/${id}/acknowledge`,
  },
  DASHBOARDS: {
    ROOT: '/dashboards/',
    TEMPLATES: '/dashboards/templates',
    INSTANTIATE: (key: string) => `/dashboards/templates/${key}/instantiate`,
    BY_ID: (id: string) => `/dashboards/${id}`,
    PUBLISH: (id: string) => `/dashboards/${id}/publish`,
    /** Restores the published version into the draft (spec §1.3). */
    DISCARD_DRAFT: (id: string) => `/dashboards/${id}/discard-draft`,
  },
  SAVED_QUERIES: {
    ROOT: '/saved-queries/',
    BY_ID: (id: string) => `/saved-queries/${id}`,
    /** Dashboards and widgets that bind this query. */
    USAGE: (id: string) => `/saved-queries/${id}/usage`,
    /** Stage 1: tap-api authorizes, then passes through to tap-query. */
    EXECUTE: (id: string) => `/saved-queries/${id}/execute`,
  },
  REPORTS: {
    ROOT: '/reports/',
    BY_ID: (id: string) => `/reports/${id}`,
    GENERATE: (id: string) => `/reports/${id}/generate`,
    RUNS: (id: string) => `/reports/${id}/runs`,
    DOWNLOAD: (id: string, runId: string) => `/reports/${id}/runs/${runId}/download`,
  },
  CONNECTORS: {
    ROOT: '/connectors/',
    BY_ID: (id: string) => `/connectors/${id}`,
    TEST: (id: string) => `/connectors/${id}/test`,
    /** Tests a configuration that has not been saved yet. */
    TEST_CONFIG: '/connectors/test',
    /** Tables + columns of the connector database (metadata only). */
    SCHEMA: (id: string) => `/connectors/${id}/schema`,
    /** Saved queries that depend on the connector. */
    USAGE: (id: string) => `/connectors/${id}/usage`,
  },
  SETTINGS: {
    ROOT: '/settings/',
    BY_KEY: (key: string) => `/settings/${key}`,
  },
  AUDIT: {
    ROOT: '/audit/',
  },
} as const
